const DIMENSIONS = {type:'Type',style:'Style',diameter:'Diameter',thread:'Thread',length:'Length',material:'Material',finish:'Finish',grade:'Grade'};
const collator = new Intl.Collator('en-US', {numeric:true,sensitivity:'base'});
const PAGE_SIZE = 24;

function dimensionNumber(value){
  const match=value.match(/^(\d+(?:\.\d+)?(?:\/\d+)?(?: \d+\/\d+)?)("| in| mm)$/);
  if(!match) return NaN;
  const number=match[1].split(' ').reduce((sum,part)=>{const [a,b]=part.split('/').map(Number);return sum+a/(b||1);},0);
  return match[2]===' mm'?number/25.4:number;
}

export function selectProducts(products, filters, query='', sort='title') {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return products.filter(product =>
    Object.entries(filters).every(([key,value]) => !value || product.fields[key] === value) &&
    words.every(word => `${product.title} ${product.sku || ''}`.toLocaleLowerCase().includes(word))
  ).sort((a,b) => (sort==='price-asc' ? a.price-b.price : sort==='price-desc' ? b.price-a.price : 0) || collator.compare(a.title,b.title) || collator.compare(a.id,b.id));
}

export function facetValues(products, filters, query, key) {
  const others = {...filters};
  delete others[key];
  const counts = new Map();
  for (const product of selectProducts(products,others,query)) {
    const value=product.fields[key];
    if(value) counts.set(value,(counts.get(value)||0)+1);
  }
  return [...counts].sort(([a],[b])=>(['diameter','length'].includes(key)?dimensionNumber(a)-dimensionNumber(b):0)||collator.compare(a,b));
}

// Every page is required. Never silently filter only the products loaded so far.
export async function loadCatalog(href, fetchPage=fetch, progress=()=>{}) {
  const current=new URL(href);
  const base=new URL(current.pathname,current.origin);
  base.searchParams.set('view','csc-fastener-index');
  base.searchParams.set('sort_by','title-ascending');
  if(current.searchParams.has('preview_theme_id')) base.searchParams.set('preview_theme_id',current.searchParams.get('preview_theme_id'));
  async function read(page) {
    const url=new URL(base);
    url.searchParams.set('page',String(page));
    const response=await fetchPage(url.href,{credentials:'same-origin',signal:AbortSignal.timeout(20000)});
    if(!response.ok) throw new Error('Catalog request failed');
    const data=await response.json();
    if(data.page!==page || !Array.isArray(data.products) || !Number.isInteger(data.pages) || !Number.isInteger(data.total) || data.total<0 || data.total>25000 || data.pages<1 || data.pages>100 || !data.collectionId) throw new Error('Invalid catalog response');
    return data;
  }
  const first=await read(1);
  const pages=[first.products];
  let next=2, loaded=first.products.length;
  progress(loaded,first.total);
  await Promise.all(Array.from({length:Math.min(3,first.pages-1)},async()=>{
    while(next<=first.pages) {
      const page=next++;
      const data=await read(page);
      if(data.collectionId!==first.collectionId || data.total!==first.total || data.pages!==first.pages) throw new Error('Catalog changed while loading');
      pages[page-1]=data.products;
      loaded+=data.products.length;
      progress(loaded,first.total);
    }
  }));
  const products=pages.flat();
  if(products.length!==first.total || new Set(products.map(p=>p.id)).size!==first.total || products.some(p=>!p.id||typeof p.title!=='string'||!p.fields||!Number.isFinite(p.price))) throw new Error('Incomplete catalog');
  return products;
}

function element(tag, text, className) {
  const node=document.createElement(tag);
  if(text!==undefined) node.textContent=text;
  if(className) node.className=className;
  return node;
}

class FastenerBrowser extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    if(this.initialized) return;
    this.initialized=true;
    this.form=this.querySelector('form');
    this.status=this.querySelector('[data-status]');
    this.results=this.querySelector('[data-results]');
    this.paging=this.querySelector('[data-pagination]');
    this.retry=this.querySelector('[data-retry]');
    this.form.addEventListener('submit',event=>event.preventDefault());
    this.form.addEventListener('change',()=>this.changed());
    this.form.querySelector('[name="q"]').addEventListener('input',()=>{
      clearTimeout(this.timer);
      this.timer=setTimeout(()=>this.changed(),180);
    });
    this.form.addEventListener('reset',event=>{
      event.preventDefault();
      clearTimeout(this.timer);
      for(const key of [...Object.keys(DIMENSIONS),'q']) this.form.elements.namedItem(key).value='';
      this.form.elements.namedItem('sort').value='title';
      this.page=1;this.render();this.saveUrl();
    });
    this.retry.addEventListener('click',()=>this.start());
    this.onPop=()=>{if(this.products){this.restoreUrl();this.render();}};
    addEventListener('popstate',this.onPop);
    this.start();
  }
  disconnectedCallback(){removeEventListener('popstate',this.onPop);clearTimeout(this.timer);}
  async start(){
    this.retry.hidden=true;
    this.querySelector('fieldset').disabled=true;
    this.status.textContent='Loading fastener filters…';
    try {
      this.products=await loadCatalog(location.href,fetch,(loaded,total)=>{this.status.textContent=`Loading fasteners: ${loaded.toLocaleString()} of ${total.toLocaleString()}`;});
      this.querySelector('fieldset').disabled=false;
      this.restoreUrl();
      this.render();
    } catch {
      this.products=null;
      this.querySelector('fieldset').disabled=true;
      this.status.textContent='Filters could not load the full collection. Retry, or browse the products below.';
      this.retry.hidden=false;
    }
  }
  state(){
    return {filters:Object.fromEntries(Object.keys(DIMENSIONS).map(key=>[key,this.form.elements.namedItem(key).value])),query:this.form.elements.namedItem('q').value,sort:this.form.elements.namedItem('sort').value};
  }
  restoreUrl(){
    const params=new URL(location.href).searchParams;
    for(const key of Object.keys(DIMENSIONS)){
      const select=this.form.elements.namedItem(key);
      select.replaceChildren(new Option('All',''));
      for(const [value] of facetValues(this.products,{},'',key)) select.add(new Option(value,value));
      const value=params.get('f.'+key)||'';
      if(value && ![...select.options].some(o=>o.value===value)) select.add(new Option(value,value));
      select.value=value;
    }
    this.form.elements.namedItem('q').value=params.get('f.q')||'';
    this.form.elements.namedItem('sort').value=['price-asc','price-desc'].includes(params.get('f.sort'))?params.get('f.sort'):'title';
    this.page=Math.max(1,Number.parseInt(params.get('f.page'),10)||1);
  }
  changed(){if(this.products){this.page=1;this.render();this.saveUrl();}}
  saveUrl(){
    const url=new URL(location.href);
    for(const key of [...Object.keys(DIMENSIONS),'q','sort','page']) url.searchParams.delete('f.'+key);
    const {filters,query,sort}=this.state();
    for(const [key,value] of Object.entries({...filters,q:query,sort:sort==='title'?'':sort,page:this.page>1?this.page:''})) if(value) url.searchParams.set('f.'+key,value);
    history.replaceState(null,'',url);
  }
  render(){
    const {filters,query,sort}=this.state();
    for(const key of Object.keys(DIMENSIONS)){
      const select=this.form.elements.namedItem(key);
      const values=facetValues(this.products,filters,query,key);
      select.replaceChildren(new Option('All',''));
      for(const [value,count] of values) select.add(new Option(`${value} (${count})`,value));
      if(filters[key]&&!values.some(([value])=>value===filters[key])) select.add(new Option(`${filters[key]} (0)`,filters[key]));
      select.value=filters[key];
    }
    const matched=selectProducts(this.products,filters,query,sort);
    const lastPage=Math.max(1,Math.ceil(matched.length/PAGE_SIZE));
    this.page=Math.min(this.page,lastPage);
    const start=(this.page-1)*PAGE_SIZE;
    this.status.textContent=matched.length?`${matched.length.toLocaleString()} products · Showing ${start+1}–${Math.min(start+PAGE_SIZE,matched.length)}`:'No products match these selections. Clear filters or try another size.';
    this.results.replaceChildren(...matched.slice(start,start+PAGE_SIZE).map(p=>this.card(p)));
    this.paging.replaceChildren();
    if(lastPage>1){
      const previous=element('button','Previous');
      const next=element('button','Next');
      previous.type=next.type='button';
      previous.disabled=this.page===1;next.disabled=this.page===lastPage;
      previous.addEventListener('click',()=>this.move(-1));next.addEventListener('click',()=>this.move(1));
      this.paging.append(previous,element('span',`Page ${this.page} of ${lastPage}`),next);
    }
  }
  move(delta){this.page+=delta;this.render();this.saveUrl();this.status.focus();this.status.scrollIntoView({block:'nearest'});}
  card(product){
    const article=element('article',undefined,'csc-fb-card');
    const link=element('a');
    const url=new URL(product.url,location.origin);
    if(url.origin!==location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid product URL');
    link.href=url.pathname+url.search;
    if(product.image){
      const imageUrl=new URL(product.image,location.origin);
      if(imageUrl.protocol==='https:' || imageUrl.origin===location.origin){
        const img=element('img');img.src=imageUrl.href;img.alt=product.title;img.loading='lazy';img.width=280;img.height=180;
        link.append(img);
      }
    }
    link.append(element('h2',product.title));
    article.append(link,element('p',[product.fields.diameter,product.fields.length,product.fields.material].filter(Boolean).join(' · '),'csc-fb-spec'),element('p',product.priceText,'csc-fb-price'));
    const detail=element('a','View product','csc-fb-view');detail.href=link.href;detail.setAttribute('aria-label',`View ${product.title}`);article.append(detail);
    return article;
  }
}
if(globalThis.customElements&&!customElements.get('csc-fastener-browser')) customElements.define('csc-fastener-browser',FastenerBrowser);
